import { addDays, addMonths, addWeeks } from 'date-fns'
import { isObject } from 'lodash'
import { Timescale } from '../Timescale'

// # ISO 8601-1 jargon
//
// Just what's relevant for this file. The descriptions are factually correct but only discuss a subset of actual
// supported syntax and meaning. (For example, dates and intervals also have representations.)
//
/** A unit of time. */
export type TimeElement = Timescale.Month | Timescale.Week | Timescale.Day
/** Unique symbol corresponding to a time element. */
type Designator = 'M' | 'W' | 'D'
/** The count of a given `TimeElement`. */
type Digits = number
/** Combination of digits and designator. Examples: `3D`, `5W`, `10M` */
type Component = `${Digits}${Designator}`
/** A duration expressed in ISO 8601-1 syntax. Examples: `P3D`, `P5W`, `P10M` */
export type Representation = `P${Component}`

/** `Temporal.Duration`’s version of `TimeElement` */
type TemporalDurationPropertyName = `${TimeElement}s`
/** The relevant subset of `Temporal.Duration`’s instance properties */
export type TemporalDurationLike = Record<TemporalDurationPropertyName, Digits>

type RegistryEntry = Readonly<{
  adder: typeof addDays
  designator: Designator
  formatter: Intl.NumberFormat
  property: TemporalDurationPropertyName
  timeElement: TimeElement
}>
const makeFormatter = (unit: TimeElement) =>
  new Intl.NumberFormat(undefined, { style: 'unit', unit, unitDisplay: 'long' })
// @note Ordered greatest to smallest to match spec
export const components: RegistryEntry[] = [
  {
    adder: addMonths,
    designator: 'M',
    formatter: makeFormatter(Timescale.Month),
    property: 'months',
    timeElement: Timescale.Month,
  },
  {
    adder: addWeeks,
    designator: 'W',
    formatter: makeFormatter(Timescale.Week),
    property: 'weeks',
    timeElement: Timescale.Week,
  },
  {
    adder: addDays,
    designator: 'D',
    formatter: makeFormatter(Timescale.Day),
    property: 'days',
    timeElement: Timescale.Day,
  },
] as const

const representationRegex = new RegExp(
  `^P(\\d+)([${components.map(({ designator }) => designator).join('')}])$`
)
export function isValidRepresentation(item: unknown): item is Representation {
  if (typeof item !== 'string') return false

  return representationRegex.test(item)
}

export function isValidTimeElement(item: unknown): item is TimeElement {
  if (typeof item !== 'string') return false

  return components.some(({ timeElement }) => item === timeElement)
}

const defaultTemporalDurationLike = Object.fromEntries(
  components.map(({ property }) => [property, 0])
) as TemporalDurationLike

const isDurationLike = (
  candidate: unknown
): candidate is TemporalDurationLike => {
  if (!isObject(candidate)) return false

  // @note Intentionally allows additional properties for graceful transition to `Temporal.Duration`
  return components.every(
    ({ property }) =>
      property in candidate &&
      Number.isInteger((candidate as Record<string, unknown>)[property])
  )
}

/**
 * Provides a subset of `Temporal.Duration.from`, which we can’t use yet.
 *
 * @note This only supports a single component.
 * @note This only supports the `TimeElement` subset of valid time elements.
 *
 * @example
 * from('P3D')
 * //=> { days: 3, weeks: 0, months: 0 }
 * from('P5W')
 * //=> { days: 0, weeks: 5, months: 0 }
 * from('P10M')
 * //=> { days: 0, weeks: 0, months: 10 }
 * from()
 * //=> TypeError
 * from(3)
 * //=> TypeError
 * from('P2Y')
 * //=> RangeError
 * from('P3M2D')
 * //=> RangeError
 */
export function from(
  item: TemporalDurationLike | Representation | string
): TemporalDurationLike {
  // @note Matches `Temporal.Duration.from` behavior that calling with a `Temporal.Duration` instance clones the
  // instance
  if (isDurationLike(item)) return { ...item }

  if (typeof item !== 'string')
    throw new TypeError('Argument must be Duration or string.')

  const match = representationRegex.exec(item)
  if (!match) throw new RangeError(`'${item}' doesn't match supported syntax`)

  const [, rawDigits, rawDesignator] = match
  const entry = components.find((e) => e.designator === rawDesignator)!

  return {
    ...defaultTemporalDurationLike,
    [entry.property]: parseInt(rawDigits, 10),
  }
}

const dayEntry = components.find((e) => e.timeElement === Timescale.Day)!
/** Go through `durationLike` to find the first non-zero `digits`, falling back to 0 days. */
export const getComponent = (
  durationLike: TemporalDurationLike
): { entry: RegistryEntry; digits: Digits } => {
  const pairs = Object.entries(durationLike)
  const invalidValue = pairs.find(([, value]) => value < 0)
  if (invalidValue)
    throw new RangeError(
      `'${invalidValue[0]}' must be at least 0; got ${invalidValue[1]}`
    )

  const withValue = pairs.filter(([, value]) => value > 0)
  if (withValue.length === 0) return { entry: dayEntry, digits: 0 }

  if (withValue.length > 1)
    throw new RangeError(
      `${withValue.length} fields have values, at most one is allowed`
    )

  const [propertyName, digits] = withValue[0]
  const entry = components.find((e) => e.property === propertyName)!
  return { entry, digits }
}

/**
 * Provides a subset of `Temporal.Duration`’s `toString`, which we can’t use yet.
 *
 * @note This only supports the `Designator` subset of valid designators.
 * @note This only supports a single component.
 */
export function toString(
  item: TemporalDurationLike | Representation | string
): Representation {
  const { entry, digits } = getComponent(from(item))
  return `P${digits}${entry.designator}`
}

/**
 * Approximation of `Temporal.PlainDate`’s `add`, which we can’t use yet.
 *
 * @example
 * add(new Date('2026-01-01'), 'P3D')
 * //=> Date 2026-01-04
 *
 * @example
 * const asDurationLike = from('P5W')
 * add(new Date('2010-10-01'), asDurationLike)
 * //=> Date 2010-11-05
 */
export function add(
  baseDate: Date,
  item: TemporalDurationLike | Representation | string
): Date {
  const durationLike = from(item)
  return components.reduce(
    (date, { property, adder }) => adder(date, durationLike[property]),
    baseDate
  )
}

/** Convert a duration representation to a (localized) human-readable string */
export function toLocaleString(
  item: TemporalDurationLike | Representation | string
): string | undefined {
  const { entry, digits } = getComponent(from(item))
  // For our purposes `0 days` isn't worth displaying
  if (digits === 0) return

  return entry.formatter.format(digits)
}
